import React, { useState, useEffect } from 'react';
import { ArrowLeft, Upload, Eye, Trash2, Settings, Users, FileText, Video, Shield, LogOut, Plus, BarChart3, AlertTriangle, Calendar } from 'lucide-react';
import { Button } from './ui/button';
import { Link } from 'react-router-dom';
import axios from 'axios';
import CommunityStats from './CommunityStats';
import UserManagement from './UserManagement';
import FlaggedContent from './FlaggedContent';
import BookingManagement from './BookingManagement';

const AdminPanel = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminUser, setAdminUser] = useState(null);
  const [adminToken, setAdminToken] = useState(localStorage.getItem('admin_token'));
  const [activeTab, setActiveTab] = useState('overview');
  const [trainingMaterials, setTrainingMaterials] = useState([]);
  
  // Login form state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  
  // Upload form state
  const [uploadForm, setUploadForm] = useState({
    title: '',
    description: '',
    type: 'video',
    category: 'Six Sigma',
    duration: ''
  });

  const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  useEffect(() => {
    if (adminToken) {
      verifyAdminSession();
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchTrainingMaterials();
    }
  }, [isAuthenticated]);

  const verifyAdminSession = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/admin/verify`, {
        params: { token: adminToken }
      });
      
      if (response.data.valid) {
        setIsAuthenticated(true);
        setAdminUser(response.data.user);
      } else {
        handleLogout();
      }
    } catch (error) {
      console.error('Session verification failed:', error);
      handleLogout();
    }
  };

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    
    try {
      const response = await axios.post(`${API_BASE}/api/admin/login`, {
        email: loginEmail,
        password: loginPassword
      });
      
      const { token, user } = response.data;
      
      localStorage.setItem('admin_token', token);
      setAdminToken(token);
      setAdminUser(user);
      setIsAuthenticated(true);
      
      setLoginEmail('');
      setLoginPassword('');
      
      alert('Admin login successful!');
    } catch (error) {
      console.error('Admin login failed:', error);
      alert(error.response?.data?.detail || 'Admin login failed. Please check your credentials.');
    }
  };

  const handleLogout = async () => {
    try {
      if (adminToken) {
        await axios.post(`${API_BASE}/api/admin/logout`, null, {
          params: { token: adminToken }
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('admin_token');
      setAdminToken(null);
      setAdminUser(null);
      setIsAuthenticated(false);
    }
  };

  const fetchTrainingMaterials = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/training/materials`);
      setTrainingMaterials(response.data.materials || []);
    } catch (error) {
      console.error('Failed to fetch training materials:', error);
    }
  };

  const handleUploadMaterial = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(`${API_BASE}/api/training/materials`, uploadForm, {
        params: { admin_token: adminToken }
      });
      
      alert('Training material uploaded successfully!');
      setUploadForm({
        title: '',
        description: '',
        type: 'video',
        category: 'Six Sigma',
        duration: ''
      });
      
      fetchTrainingMaterials();
    } catch (error) {
      console.error('Upload failed:', error);
      alert(error.response?.data?.detail || 'Upload failed. Please try again.');
    }
  };

  const deleteMaterial = async (materialId) => {
    if (!window.confirm('Are you sure you want to delete this material?')) {
      return;
    }
    
    try {
      await axios.delete(`${API_BASE}/api/training/materials/${materialId}`, {
        params: { admin_token: adminToken }
      });
      
      alert('Material deleted successfully!');
      fetchTrainingMaterials();
    } catch (error) {
      console.error('Delete failed:', error);
      alert('Failed to delete material. Please try again.');
    }
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="dark-container min-h-screen" style={{ paddingTop: '100px' }}>
        <div className="max-w-md mx-auto px-6 py-8">
          <Link to="/community">
            <Button className="btn-secondary mb-8">
              <ArrowLeft size={20} />
              Back to Community
            </Button>
          </Link>

          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-turquoise-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield size={40} className="text-turquoise-400" />
            </div>
            <h1 className="display-large mb-4">
              Admin <span className="turquoise-text-gradient">Panel</span>
            </h1>
            <p className="body-large text-gray-300">
              Secure access for administrators
            </p>
          </div>

          <form onSubmit={handleAdminLogin} className="bg-gray-900/50 border border-turquoise-500/20 rounded-16 p-6">
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Admin Email</label>
                <input
                  type="email"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  required
                  className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-white"
                  placeholder="Enter admin email"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Password</label>
                <input
                  type="password"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  required
                  className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-white"
                  placeholder="Enter password"
                />
              </div>
            </div>
            
            <Button type="submit" className="btn-primary-enhanced w-full">
              <Shield size={20} />
              Admin Login
            </Button>
          </form>

          <div className="text-center mt-4">
            <Link to="/forgot-password">
              <Button className="btn-secondary w-full">
                Forgot Admin Password?
              </Button>
            </Link>
          </div>

          <div className="mt-6 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
            <p className="text-yellow-400 text-sm">
              <strong>Note:</strong> Only users with admin role can access this panel. 
              Regular community users need to be promoted to admin status first.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="dark-container min-h-screen" style={{ paddingTop: '100px' }}>
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Link to="/community">
            <Button className="btn-secondary">
              <ArrowLeft size={20} />
              Back to Community
            </Button>
          </Link>
          
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-white font-medium">Welcome, {adminUser?.name}</p>
              <p className="text-gray-400 text-sm">Administrator</p>
            </div>
            <Button onClick={handleLogout} className="btn-secondary">
              <LogOut size={20} />
              Logout
            </Button>
          </div>
        </div>

        <div className="text-center mb-8">
          <h1 className="display-large mb-4">
            Admin <span className="turquoise-text-gradient">Dashboard</span>
          </h1>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-4 mb-8 border-b border-gray-800">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-6 py-3 font-medium transition-all duration-200 border-b-2 ${
              activeTab === 'overview'
                ? 'text-turquoise-400 border-turquoise-400'
                : 'text-gray-400 border-transparent hover:text-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <Settings size={20} />
              <span>Overview</span>
            </div>
          </button>

          <button
            onClick={() => setActiveTab('analytics')}
            className={`px-6 py-3 font-medium transition-all duration-200 border-b-2 ${
              activeTab === 'analytics'
                ? 'text-turquoise-400 border-turquoise-400'
                : 'text-gray-400 border-transparent hover:text-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <BarChart3 size={20} />
              <span>Community Analytics</span>
            </div>
          </button>
          
          <button
            onClick={() => setActiveTab('training')}
            className={`px-6 py-3 font-medium transition-all duration-200 border-b-2 ${
              activeTab === 'training'
                ? 'text-turquoise-400 border-turquoise-400'
                : 'text-gray-400 border-transparent hover:text-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <Upload size={20} />
              <span>Training Materials</span>
            </div>
          </button>

          <button
            onClick={() => setActiveTab('users')}
            className={`px-6 py-3 font-medium transition-all duration-200 border-b-2 ${
              activeTab === 'users'
                ? 'text-turquoise-400 border-turquoise-400'
                : 'text-gray-400 border-transparent hover:text-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <Users size={20} />
              <span>Users</span>
            </div>
          </button>

          <button
            onClick={() => setActiveTab('moderation')}
            className={`px-6 py-3 font-medium transition-all duration-200 border-b-2 ${
              activeTab === 'moderation'
                ? 'text-turquoise-400 border-turquoise-400'
                : 'text-gray-400 border-transparent hover:text-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <AlertTriangle size={20} />
              <span>Moderation</span>
            </div>
          </button>

          <button
            onClick={() => setActiveTab('bookings')}
            className={`px-6 py-3 font-medium transition-all duration-200 border-b-2 ${
              activeTab === 'bookings'
                ? 'text-turquoise-400 border-turquoise-400'
                : 'text-gray-400 border-transparent hover:text-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <Calendar size={20} />
              <span>Bookings</span>
            </div>
          </button>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-gray-900/50 border border-turquoise-500/20 rounded-16 p-6">
              <div className="flex items-center gap-3 mb-4">
                <Users size={24} className="text-blue-400" />
                <h3 className="heading-3 text-white">Community Management</h3>
              </div>
              <p className="text-gray-400 mb-4">Manage community members and posts</p>
              <Button className="btn-secondary w-full">
                View Community Stats
              </Button>
            </div>

            <div className="bg-gray-900/50 border border-turquoise-500/20 rounded-16 p-6">
              <div className="flex items-center gap-3 mb-4">
                <Video size={24} className="text-green-400" />
                <h3 className="heading-3 text-white">Training Materials</h3>
              </div>
              <p className="text-gray-400 mb-4">Upload and manage training content</p>
              <Button 
                className="btn-primary-enhanced w-full"
                onClick={() => setActiveTab('training')}
              >
                Manage Materials
              </Button>
            </div>

            <div className="bg-gray-900/50 border border-turquoise-500/20 rounded-16 p-6">
              <div className="flex items-center gap-3 mb-4">
                <Shield size={24} className="text-purple-400" />
                <h3 className="heading-3 text-white">System Settings</h3>
              </div>
              <p className="text-gray-400 mb-4">Configure system preferences</p>
              <Button className="btn-secondary w-full">
                System Config
              </Button>
            </div>
          </div>
        )}

        {/* Community Analytics Tab */}
        {activeTab === 'analytics' && (
          <div className="space-y-6">
            <CommunityStats />
          </div>
        )}

        {/* Training Materials Tab */}
        {activeTab === 'training' && (
          <div className="space-y-8">
            {/* Upload Form */}
            <div className="bg-gray-900/50 border border-turquoise-500/20 rounded-16 p-6">
              <h3 className="heading-3 text-white mb-6">Upload New Material</h3>
              
              <form onSubmit={handleUploadMaterial} className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Title</label>
                  <input
                    type="text"
                    value={uploadForm.title}
                    onChange={(e) => setUploadForm({...uploadForm, title: e.target.value})}
                    required
                    className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-white"
                    placeholder="Material title"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Type</label>
                  <select
                    value={uploadForm.type}
                    onChange={(e) => setUploadForm({...uploadForm, type: e.target.value})}
                    className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-white"
                  >
                    <option value="video">Training Video</option>
                    <option value="document">Document</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Category</label>
                  <select
                    value={uploadForm.category}
                    onChange={(e) => setUploadForm({...uploadForm, category: e.target.value})}
                    className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-white"
                  >
                    <option value="Six Sigma">Six Sigma</option>
                    <option value="ISO Standards">ISO Standards</option>
                    <option value="Lean">Lean</option>
                    <option value="Automation">Automation</option>
                    <option value="Process Excellence">Process Excellence</option>
                  </select>
                </div>

                {uploadForm.type === 'video' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Duration</label>
                    <input
                      type="text"
                      value={uploadForm.duration}
                      onChange={(e) => setUploadForm({...uploadForm, duration: e.target.value})}
                      className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-white"
                      placeholder="e.g., 45 mins"
                    />
                  </div>
                )}

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
                  <textarea
                    value={uploadForm.description}
                    onChange={(e) => setUploadForm({...uploadForm, description: e.target.value})}
                    required
                    rows="3"
                    className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-white"
                    placeholder="Material description"
                  />
                </div>

                <div className="md:col-span-2">
                  <Button type="submit" className="btn-primary-enhanced">
                    <Plus size={20} />
                    Upload Material
                  </Button>
                </div>
              </form>
            </div>

            {/* Materials List */}
            <div className="bg-gray-900/50 border border-turquoise-500/20 rounded-16 p-6">
              <h3 className="heading-3 text-white mb-6">Uploaded Materials ({trainingMaterials.length})</h3>
              
              {trainingMaterials.length === 0 ? (
                <p className="text-gray-400 text-center py-8">No materials uploaded yet.</p>
              ) : (
                <div className="space-y-4">
                  {trainingMaterials.map((material) => (
                    <div key={material.id} className="bg-gray-800 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 bg-turquoise-500/20 rounded-lg flex items-center justify-center">
                            {material.type === 'video' ? (
                              <Video size={24} className="text-turquoise-400" />
                            ) : (
                              <FileText size={24} className="text-turquoise-400" />
                            )}
                          </div>
                          
                          <div>
                            <h4 className="font-semibold text-white">{material.title}</h4>
                            <p className="text-gray-400 text-sm mt-1">{material.description}</p>
                            <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                              <span>Category: {material.category}</span>
                              <span>Type: {material.type}</span>
                              {material.duration && <span>Duration: {material.duration}</span>}
                              <span>Uploaded: {new Date(material.created_at).toLocaleDateString()}</span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Button className="btn-secondary p-2">
                            <Eye size={16} />
                          </Button>
                          <Button 
                            className="btn-secondary p-2 text-red-400 hover:bg-red-500/20"
                            onClick={() => deleteMaterial(material.id)}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <UserManagement API_BASE={API_BASE} />
        )}

        {/* Moderation Tab */}
        {activeTab === 'moderation' && (
          <FlaggedContent API_BASE={API_BASE} />
        )}

        {/* Bookings Tab */}
        {activeTab === 'bookings' && (
          <BookingManagement API_BASE={API_BASE} />
        )}
      </div>
    </div>
  );
};

export default AdminPanel;